import axios from 'axios';
import { BankCard, OtpMedium, RequestOtpResponse } from '../types';
import { getApiConfig } from '../utils/config';

const apiConfig = getApiConfig();
// Base URL now includes /api/card-view-request
const API_BASE = apiConfig.baseUrl;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request/response interceptors for debugging
api.interceptors.request.use(
  (config) => {
    if (apiConfig.environment === 'local') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (apiConfig.environment === 'local') {
      console.log(`API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase();
    
    // Always log errors, but with more detail for unexpected statuses
    if (status && status !== 401 && status !== 404) {
      console.error(`API Error Response [${method} ${url}]:`, {
        status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
    } else if (apiConfig.environment === 'local') {
      console.error(`API Response Error: ${status} ${method} ${url}`);
    }
    
    return Promise.reject(error);
  }
);

export const cardViewApi = {
  validate: async (cvrt: string): Promise<string> => {
    const response = await api.get<string>(`/${cvrt}/validate`);
    return response.data;
  },

  requestOtp: async (cvrt: string, phoneNumber: string, medium: OtpMedium = 'sms'): Promise<RequestOtpResponse> => {
    const response = await api.post<RequestOtpResponse>(
      `/${cvrt}/requestotp?medium=${medium}`,
      JSON.stringify(phoneNumber)
    );
    return response.data;
  },

  getCard: async (cvrt: string, otp: string): Promise<BankCard> => {
    const response = await api.post<any>(`/${cvrt}/card`, JSON.stringify(otp));
    
    // Map backend response to frontend interface
    const data = response.data;
    if (apiConfig.environment === 'local') {
      console.log('Raw card response from backend:', data);
    }

    // Handle both possible field names (capitalized from backend, lowercase from mock)
    const mappedCard: BankCard = {
      // Map from backend field names to frontend field names
      cardNumber: data.Number || data.number || data.cardNumber || '',
      cardholderName: data.OwnerName || data.ownerName || data.cardholderName || '',
      expiryMonth: String(data.ExpirationMonth || data.expirationMonth || data.expiryMonth || ''),
      expiryYear: String(data.ExpirationYear || data.expirationYear || data.expiryYear || ''),
      cvv: data.CVV || data.cvv || '***', // Backend might not send CVV for security
      cardType: data.CardType || data.cardType || '',
      brand: data.Brand || data.brand || data.CardType || data.cardType || '',
      // Keep original fields for debugging
      Number: data.Number,
      OwnerName: data.OwnerName,
      ExpirationMonth: data.ExpirationMonth,
      ExpirationYear: data.ExpirationYear,
      CardType: data.CardType,
    };

    if (apiConfig.environment === 'local') {
      console.log('Mapped card data:', mappedCard);
    }
    return mappedCard;
  },
};

export default api;